import type { AdminAccount, CreateAdminRequest } from '@inflip/contracts/admin';
import { UserRole } from '@inflip/shared';
import { useCallback, useEffect, useState } from 'react';

import { Select } from '../components/select';
import {
  Button,
  ConfirmDialog,
  ErrorBanner,
  PageHeader,
  PromptDialog,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';

type DialogState =
  | null
  | { kind: 'resetPassword'; account: AdminAccount }
  | { kind: 'deactivate'; account: AdminAccount }
  | { kind: 'resetMfa'; account: AdminAccount };

/** 어드민 계정 관리 — SUPER_ADMIN 전용 (일반 ADMIN 은 403) */
export function AdminAccountsPage() {
  const { session } = useAdminAuth();
  const [rows, setRows] = useState<AdminAccount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateAdminRequest>({
    email: '',
    name: '',
    password: '',
    role: UserRole.ADMIN,
  });
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  // 내 계정 — 본인 OTP 초기화는 안내를 달리한다
  const [myId, setMyId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listAdmins(session.accessToken)
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!session) return;
    api
      .me(session.accessToken)
      .then((me) => setMyId(me.id))
      .catch(() => undefined);
  }, [session]);

  async function run(fn: () => Promise<unknown>) {
    if (!session || pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  if (!session) return null;
  const token = session.accessToken;

  return (
    <main className="mx-auto w-full max-w-[880px] px-6 py-8">
      <PageHeader
        title="어드민 계정"
        description="어드민 계정 생성과 비밀번호 재설정·OTP 초기화·비활성화를 처리해요"
        actions={
          <Button
            variant={showCreate ? 'secondary' : 'primary'}
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? '닫기' : '+ 어드민 추가'}
          </Button>
        }
      />
      {error && <ErrorBanner message={error} />}

      {showCreate && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api.createAdmin(token, form);
              setShowCreate(false);
              setForm({ email: '', name: '', password: '', role: UserRole.ADMIN });
            });
          }}
          className="mb-6 flex flex-wrap items-end gap-3 rounded-lg bg-muted/60 p-5"
        >
          <div>
            <label className="text-xs font-semibold text-muted-foreground">이메일</label>
            <input
              type="email"
              className={`mt-1 block w-56 ${inputCls}`}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">이름</label>
            <input
              className={`mt-1 block w-32 ${inputCls}`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              초기 비밀번호 (10자+)
            </label>
            <input
              type="password"
              className={`mt-1 block w-44 ${inputCls}`}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              minLength={10}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">권한</label>
            <Select
              label="권한"
              className="mt-1 w-64"
              value={form.role}
              options={[
                { value: UserRole.ADMIN, label: 'ADMIN (운영)' },
                { value: UserRole.SUPER_ADMIN, label: 'SUPER_ADMIN (계정 관리 포함)' },
              ]}
              onValueChange={(v) => setForm({ ...form, role: v as CreateAdminRequest['role'] })}
            />
          </div>
          <Button type="submit" disabled={pending}>
            생성
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            관리자 2단계 OTP 인증이 켜져 있으면 첫 로그인 때 OTP 앱 등록이 요구돼요. 초기 비밀번호는
            직접 전달하고 바로 변경하도록 안내해주세요.
          </p>
        </form>
      )}

      <div className="divide-y divide-border/60">
        {rows.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 py-4 text-sm">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-muted text-sm font-bold text-muted-foreground">
              {a.name.slice(0, 1)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold">
                {a.name}
                <span
                  className={`ml-1.5 text-xs font-bold ${
                    a.role === UserRole.SUPER_ADMIN ? 'text-brand' : 'text-blue-600'
                  }`}
                >
                  {a.role === UserRole.SUPER_ADMIN ? '슈퍼어드민' : '어드민'}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {a.email} · OTP {a.mfaEnrolled ? '등록됨' : '미등록'}
              </p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() => setDialog({ kind: 'resetPassword', account: a })}
              >
                비밀번호 재설정
              </Button>
              {a.mfaEnrolled && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending}
                  onClick={() => setDialog({ kind: 'resetMfa', account: a })}
                >
                  OTP 초기화
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                disabled={pending}
                onClick={() => setDialog({ kind: 'deactivate', account: a })}
              >
                비활성화
              </Button>
            </div>
          </div>
        ))}
      </div>

      {dialog?.kind === 'resetPassword' && (
        <PromptDialog
          title="비밀번호를 재설정할까요?"
          description={`${dialog.account.email} 의 새 비밀번호를 입력해주세요 (10자 이상).`}
          placeholder="새 비밀번호"
          submitLabel="재설정"
          busy={pending}
          onSubmit={(pw) =>
            void run(() => api.resetAdminPassword(token, dialog.account.id, { password: pw }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'resetMfa' && (
        <ConfirmDialog
          title={dialog.account.id === myId ? '내 OTP 를 초기화할까요?' : 'OTP 를 초기화할까요?'}
          description={
            dialog.account.id === myId
              ? '내 OTP 등록이 지워지고 이 브라우저를 뺀 다른 로그인이 모두 끝나요. 2단계 OTP 인증이 켜져 있으면 다음 로그인에서 새 휴대폰으로 다시 등록해야 해요.'
              : `${dialog.account.email} 의 OTP 등록이 지워지고 로그인이 모두 끝나요. 휴대폰을 잃어버렸을 때 쓰고, 다음 로그인에서 다시 등록해요.`
          }
          confirmLabel="OTP 초기화"
          danger
          busy={pending}
          onConfirm={() =>
            void run(async () => {
              await api.resetAdminMfa(token, dialog.account.id);
              showToast('OTP 를 초기화했어요');
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'deactivate' && (
        <ConfirmDialog
          title="계정을 비활성화할까요?"
          description={`${dialog.account.email} 은 즉시 로그인이 차단돼요.`}
          confirmLabel="비활성화"
          danger
          busy={pending}
          onConfirm={() => void run(() => api.deactivateAdmin(token, dialog.account.id))}
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
